﻿import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Input, Typography } from "@mui/material";

import { UploadIcon } from "@/ImageRepo";

/**
 * Handles when files are selected by browse files
 */
const ImportInput = ({ onFilesSelected, accept }) => {
  return (
    <Input
      type="file"
      name="Files"
      onChange={(event) => {
        if (onFilesSelected) {
          onFilesSelected({ newFiles: event.target.files });
        }
      }}
      onClick={(event) => {
        event.target.value = null;
      }}
      multiple={true}
      inputProps={{
        multiple: true,
        accept: accept,
      }}
      sx={{ display: "none" }}
    />
  );
};

/**
 * Drag and drop file import component
 * @param {function} onFilesSelected: Parent function that is returned list of newFiles
 * @param {string} accept: Supported file type extensions
 * @param {string} supportedFileMessage: Display message indicating what extensions are supported
 * @returns list of input files as newFiles parameter to parent function
 */
const ShepDragDropUpload = ({
  onFilesSelected,
  accept,
  supportedFileMessage,
}) => {
  // Whether user is currently dragging file over input area to set styling
  const [isFileDragging, setIsFileDragging] = useState(false);

  // Handles when file is being dragged over input area (drop will trigger upload)
  const handleFileDragOver = (event) => {
    event.preventDefault(); // so that file does not open
    event.stopPropagation();
    setIsFileDragging(true);
  };

  // Handles when dragged file leaves the input area (drop will not trigger upload)
  const handleFileDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsFileDragging(false);
  };

  // Handles when user releases file and drops into area
  const handleFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Send the selected files to parent component and trigger function if available
    if (isFileDragging && onFilesSelected) {
      onFilesSelected({ newFiles: event.dataTransfer.files });
    }
    setIsFileDragging(false);
  };

  return (
    <>
      <Box
        className="flex-center-aligned"
        component="label"
        onDragOver={handleFileDragOver}
        onDragLeave={handleFileDragLeave}
        onDrop={handleFileDrop}
        sx={{
          height: "300px",
          width: "100%",
          borderColor: isFileDragging ? "var(--tertiary-13)" : "transparent",
          border: isFileDragging ? "2px dashed" : "2px dashed transparent",
          backgroundColor: isFileDragging
            ? "var(--tertiary-13)"
            : "var(--tertiary-14)",
          flexGrow: "1",
          flexDirection: "column",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        <UploadIcon />
        <Typography variant="contentAccent1" style={{ marginTop: "20px" }}>
          Drag & drop files or Browse
        </Typography>
        {accept !== "" ? (
          <Typography style={{ marginTop: "10px" }}>
            Supported formats: {supportedFileMessage}
          </Typography>
        ) : null}
        <ImportInput onFilesSelected={onFilesSelected} accept={accept} />
      </Box>
    </>
  );
};

ShepDragDropUpload.propTypes = {
  onFilesSelected: PropTypes.func,
  accept: PropTypes.string, // Supported file type extensions
  supportedFileMessage: PropTypes.string, // display message indicating what extensions are supported
};

ShepDragDropUpload.defaultProps = {
  accept: "", // accept all file types by default
  supportedFileMessage: "",
};

export default ShepDragDropUpload;
